import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { admin } from '../../../services/api';
import type { AdminUsageByModelItem, Pagination } from '../../../types/api';
import DatePicker from '../../ui/DatePicker';
import { getDefaultTrainingDateRangeYmd } from '../../../utils/defaultTrainingDateRange';
import { clampUsageLimit, isReversedRange, usageRangeParams } from '../../../utils/usageContract';
import { ChartCard, RankedBars } from './UsageCharts';
import { rank } from './usageChartData';
import './UsageBilling.css';

/**
 * Разрез расхода по паре «пользователь × модель» (AIT-258).
 *
 * До этой ручки разбивка по модели не была доступна нигде: `/usage/stats`
 * отдаёт суммы без разреза, а `/admin/usage` — по пользователю целиком. Здесь
 * видно, какая модель съела бюджет, а не только у кого он ушёл.
 */

const PAGE_SIZE = 20;
const DEFAULT_DATES = getDefaultTrainingDateRangeYmd();

type SortKey = 'email' | 'model_name' | 'prompt_tokens' | 'completion_tokens' | 'total_tokens' | 'cached_tokens' | 'total_cost' | 'records_count';
type SortDir = 'asc' | 'desc';

const COLUMNS: { key: SortKey; label: string; numeric: boolean }[] = [
  { key: 'email', label: 'User', numeric: false },
  { key: 'model_name', label: 'Model', numeric: false },
  { key: 'prompt_tokens', label: 'Prompt', numeric: true },
  { key: 'completion_tokens', label: 'Completion', numeric: true },
  { key: 'cached_tokens', label: 'Cached', numeric: true },
  { key: 'total_tokens', label: 'Total tokens', numeric: true },
  { key: 'total_cost', label: 'Cost', numeric: true },
  { key: 'records_count', label: 'Records', numeric: true },
];

const num = (v: unknown): number => (typeof v === 'number' && Number.isFinite(v) ? v : 0);
const fmtInt = (v: unknown): string => num(v).toLocaleString('en-US');
const fmtCost = (v: unknown): string => (typeof v === 'number' && Number.isFinite(v) ? `$${v.toFixed(v < 1 ? 4 : 2)}` : '—');

interface Props {
  /** Панель сама ошибки наружу не бросает — показывает их на месте. */
  onError?: (message: string | null) => void;
  /** Меняется, когда в шапке раздела нажали Refresh. */
  refreshToken?: number;
}

const UsageByModelPanel: React.FC<Props> = ({ onError, refreshToken = 0 }) => {
  const [items, setItems] = useState<AdminUsageByModelItem[]>([]);
  const [pagination, setPagination] = useState<Pagination | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState(1);

  const [dateFrom, setDateFrom] = useState(DEFAULT_DATES.from);
  const [dateTo, setDateTo] = useState(DEFAULT_DATES.to);
  const [userId, setUserId] = useState('');
  const [excludeService, setExcludeService] = useState(false);

  const [applied, setApplied] = useState({
    from: DEFAULT_DATES.from,
    to: DEFAULT_DATES.to,
    userId: '',
    excludeService: false,
  });

  const [sortKey, setSortKey] = useState<SortKey>('total_tokens');
  const [sortDir, setSortDir] = useState<SortDir>('desc');

  const fail = useCallback(
    (message: string | null) => {
      setError(message);
      onError?.(message);
    },
    [onError]
  );

  const load = useCallback(
    async (targetPage: number) => {
      setLoading(true);
      fail(null);
      try {
        const params: Record<string, unknown> = {
          limit: clampUsageLimit(PAGE_SIZE),
          offset: (targetPage - 1) * PAGE_SIZE,
          ...usageRangeParams(applied.from, applied.to),
        };
        if (applied.userId.trim()) params.user_id = applied.userId.trim();
        if (applied.excludeService) params.exclude_service_usage = true;

        const resp = await admin.getUsageByModel(params as Parameters<typeof admin.getUsageByModel>[0]);
        if (resp?.success && resp.data) {
          setItems(Array.isArray(resp.data.items) ? resp.data.items : []);
          setPagination(resp.data.pagination ?? null);
          setPage(targetPage);
        } else {
          setItems([]);
          fail(resp?.error?.message || 'Failed to load usage by model.');
        }
      } catch (e) {
        const message =
          (e as { response?: { data?: { error?: { message?: string } } } })?.response?.data?.error?.message ||
          (e as Error)?.message ||
          'Failed to load usage by model.';
        setItems([]);
        fail(message);
      } finally {
        setLoading(false);
      }
    },
    [applied, fail]
  );

  useEffect(() => {
    void load(1);
  }, [load]);

  // Обновление из шапки раздела. Зависеть от `load`/`page` здесь нельзя:
  // эффект срабатывал бы на каждую смену фильтров, а не на нажатие кнопки.
  useEffect(() => {
    if (!refreshToken) return;
    void load(page);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refreshToken]);

  const applyFilters = () => {
    if (isReversedRange(dateFrom, dateTo)) {
      fail('Invalid date range: From must be before or equal to To.');
      return;
    }
    setApplied({ from: dateFrom, to: dateTo, userId, excludeService });
  };

  const resetFilters = () => {
    setDateFrom(DEFAULT_DATES.from);
    setDateTo(DEFAULT_DATES.to);
    setUserId('');
    setExcludeService(false);
    setApplied({ from: DEFAULT_DATES.from, to: DEFAULT_DATES.to, userId: '', excludeService: false });
  };

  const toggleSort = (key: SortKey) => {
    if (key === sortKey) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
      return;
    }
    setSortKey(key);
    // Числовые колонки интереснее сверху вниз, текстовые — по алфавиту.
    setSortDir(COLUMNS.find((c) => c.key === key)?.numeric ? 'desc' : 'asc');
  };

  const sorted = useMemo(() => {
    const rows = [...items];
    const dir = sortDir === 'asc' ? 1 : -1;
    rows.sort((a, b) => {
      if (sortKey === 'email' || sortKey === 'model_name') {
        const av = (a[sortKey] ?? '').toString().toLowerCase();
        const bv = (b[sortKey] ?? '').toString().toLowerCase();
        return av.localeCompare(bv) * dir;
      }
      return (num(a[sortKey]) - num(b[sortKey])) * dir;
    });
    return rows;
  }, [items, sortKey, sortDir]);

  /** Итоги считаются по странице — ручка не отдаёт агрегат, и придумывать его нельзя. */
  const pageTotals = useMemo(
    () =>
      sorted.reduce(
        (acc, row) => ({
          total: acc.total + num(row.total_tokens),
          cached: acc.cached + num(row.cached_tokens),
          cost: acc.cost + num(row.total_cost),
          records: acc.records + num(row.records_count),
          models: acc.models.add((row.model_name ?? '—').toLowerCase()),
        }),
        { total: 0, cached: 0, cost: 0, records: 0, models: new Set<string>() }
      ),
    [sorted]
  );

  // Два разреза одной страницы: чем платим (модель) и кто платит (пользователь).
  const byModel = useMemo(
    () => rank(sorted, (r) => (r.model_name ?? '—').toLowerCase(), (r) => r.model_name ?? 'not reported'),
    [sorted]
  );
  const byUser = useMemo(() => rank(sorted, (r) => r.user_id, (r) => r.email || r.user_id), [sorted]);

  const total = pagination?.total ?? 0;
  const pages = Math.max(1, Math.ceil(total / PAGE_SIZE));
  const cachedShare = pageTotals.total > 0 ? Math.round((pageTotals.cached / pageTotals.total) * 100) : 0;

  return (
    <div className="ub">
      <div className="ub-bar">
        <div className="ub-field">
          <span className="ub-field__label">From</span>
          <DatePicker value={dateFrom} onChange={setDateFrom} max={dateTo || undefined} />
        </div>
        <div className="ub-field">
          <span className="ub-field__label">To</span>
          <DatePicker value={dateTo} onChange={setDateTo} min={dateFrom || undefined} />
        </div>
        <div className="ub-field">
          <span className="ub-field__label">User ID</span>
          <input
            className="ub-input"
            value={userId}
            onChange={(e) => setUserId(e.target.value)}
            placeholder="all users"
            style={{ minWidth: 250 }}
          />
        </div>
        <label className="ub-check">
          <input type="checkbox" checked={excludeService} onChange={(e) => setExcludeService(e.target.checked)} />
          Exclude service usage
        </label>
        <div className="ub-bar__actions">
          <button type="button" className="ub-btn" onClick={resetFilters} disabled={loading}>
            Reset
          </button>
          <button type="button" className="ub-btn ub-btn--primary" onClick={applyFilters} disabled={loading}>
            Apply
          </button>
        </div>
      </div>

      <div className="ub-kpis">
        <div className="ub-kpi">
          <span className="ub-kpi__label">Rows total</span>
          <span className="ub-kpi__value">{fmtInt(total)}</span>
          <span className="ub-kpi__hint">user × model pairs in range</span>
        </div>
        <div className="ub-kpi">
          <span className="ub-kpi__label">Tokens on page</span>
          <span className="ub-kpi__value">{fmtInt(pageTotals.total)}</span>
          <span className="ub-kpi__hint">{`page ${page} of ${pages}`}</span>
        </div>
        <div className="ub-kpi">
          <span className="ub-kpi__label">Cached</span>
          <span className="ub-kpi__value">{`${cachedShare}%`}</span>
          <span className="ub-kpi__hint">{`${fmtInt(pageTotals.cached)} tokens served from cache`}</span>
        </div>
        <div className="ub-kpi">
          <span className="ub-kpi__label">Cost on page</span>
          <span className="ub-kpi__value">{fmtCost(pageTotals.cost)}</span>
          <span className="ub-kpi__hint">{`${pageTotals.models.size} model(s), ${fmtInt(pageTotals.records)} records`}</span>
        </div>
      </div>


      {loading || sorted.length === 0 ? null : (
        <div className="ub-charts">
          <ChartCard title="Top models" hint="by tokens on this page">
            <RankedBars slices={byModel} emptyText="No model breakdown for this page." />
          </ChartCard>
          <ChartCard title="Top users" hint="by tokens on this page">
            <RankedBars slices={byUser} emptyText="No user breakdown for this page." />
          </ChartCard>
        </div>
      )}
      {error ? (
        <div className="ub-error" role="alert">
          <i className="fas fa-triangle-exclamation" aria-hidden="true" />
          <div>{error}</div>
          <button type="button" className="ub-btn" onClick={() => void load(page)}>
            Retry
          </button>
        </div>
      ) : null}

      <div className="ub-table-wrap">
        <div className="ub-table-scroll">
          <table className="ub-table">
            <thead>
              <tr>
                {COLUMNS.map((col) => {
                  const on = sortKey === col.key;
                  return (
                    <th
                      key={col.key}
                      className={col.numeric ? 'ub-num' : undefined}
                      aria-sort={on ? (sortDir === 'asc' ? 'ascending' : 'descending') : 'none'}
                    >
                      <button
                        type="button"
                        className={`ub-sort${on ? ' ub-sort--on' : ''}`}
                        onClick={() => toggleSort(col.key)}
                        title={`Sort by ${col.label}`}
                      >
                        {col.label}
                        <i
                          className={`fas ${on && sortDir === 'asc' ? 'fa-arrow-up-short-wide' : 'fa-arrow-down-wide-short'} ub-sort__icon`}
                          aria-hidden="true"
                        />
                      </button>
                    </th>
                  );
                })}
              </tr>
            </thead>
            <tbody>
              {loading
                ? Array.from({ length: 6 }).map((_, i) => (
                    <tr key={`sk-${i}`}>
                      <td colSpan={COLUMNS.length}>
                        <div className="ub-skeleton-row">
                          <span className="ub-skeleton" style={{ flex: 2 }} />
                          <span className="ub-skeleton" style={{ flex: 1 }} />
                          <span className="ub-skeleton" style={{ flex: 1 }} />
                          <span className="ub-skeleton" style={{ flex: 1 }} />
                        </div>
                      </td>
                    </tr>
                  ))
                : sorted.map((row, i) => (
                    <tr key={`${row.user_id}-${row.model_name ?? 'none'}-${i}`}>
                      <td>
                        <div className="ub-email">{row.email || '—'}</div>
                        <div className="ub-muted" style={{ fontSize: 11 }}>
                          {row.user_id}
                        </div>
                      </td>
                      <td>
                        {row.model_name ? (
                          <span className="ub-model">{row.model_name}</span>
                        ) : (
                          <span className="ub-model ub-model--none" title="Provider did not report a model name">
                            not reported
                          </span>
                        )}
                      </td>
                      <td className="ub-num">{fmtInt(row.prompt_tokens)}</td>
                      <td className="ub-num">{fmtInt(row.completion_tokens)}</td>
                      <td className="ub-num ub-muted">{fmtInt(row.cached_tokens)}</td>
                      <td className="ub-num" style={{ fontWeight: 700 }}>
                        {fmtInt(row.total_tokens)}
                      </td>
                      <td className="ub-num">{fmtCost(row.total_cost)}</td>
                      <td className="ub-num ub-muted">{fmtInt(row.records_count)}</td>
                    </tr>
                  ))}
            </tbody>
          </table>
        </div>

        {!loading && sorted.length === 0 && !error ? (
          <div className="ub-empty">
            <i className="fas fa-chart-pie" aria-hidden="true" />
            <div className="ub-empty__title">No usage in this range</div>
            <div className="ub-empty__hint">
              Shadow rows written by ML for reconciliation are excluded from this report by design, so a quiet period
              really does look empty here. Widen the dates or clear the user filter.
            </div>
          </div>
        ) : null}
      </div>

      <div className="ub-pager">
        <span>
          {total > 0
            ? `Showing ${(page - 1) * PAGE_SIZE + 1}–${Math.min(page * PAGE_SIZE, total)} of ${fmtInt(total)}`
            : 'Nothing to show'}
        </span>
        {/* Сортировка клиентская: ручка не принимает порядок, поэтому переставляется
            только загруженная страница. Говорим об этом прямо, чтобы цифры не
            читались как «топ по всему периоду». */}
        <span className="ub-muted">· sorted within this page</span>
        <span className="ub-pager__spacer" />
        <button type="button" className="ub-btn" onClick={() => void load(page - 1)} disabled={loading || page <= 1}>
          Previous
        </button>
        <span>{`${page} / ${pages}`}</span>
        <button type="button" className="ub-btn" onClick={() => void load(page + 1)} disabled={loading || page >= pages}>
          Next
        </button>
      </div>
    </div>
  );
};

export default UsageByModelPanel;
